if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.colorOptionHandles = [
          'color',
          'colour',
          'couleur',
          'farbe',
          'farg',
          'farge',
          'finish',
          'interior-color',
          'interior-colour',
        ];
        this.hardwareColorOptionHandles = ['hardware-color', 'hardware-colour', 'hardware'];
        this.genericMediaTerms = ['apple', 'watch', 'band', 'strap', 'case', 'color', 'colour', 'finish', 'titanium'];
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initializeGallery();
      }

      connectedCallback() {
        this.initializeGallery();
        if (!this.initialized) return;

        const selectedMediaId =
          this.dataset.selectedMediaId &&
          this.querySelector(`.product__media-list > [data-media-id="${this.dataset.selectedMediaId}"]`)
            ? this.dataset.selectedMediaId
            : null;
        const activeMediaId = this.getActiveMediaItem()?.dataset.mediaId;
        const initialMediaId = selectedMediaId || activeMediaId;
        if (initialMediaId) {
          this.setActiveMedia(initialMediaId, false);
          return;
        }

        this.syncActiveMediaState();
      }

      initializeGallery() {
        if (this.initialized) return;

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        if (!this.elements.viewer) return;
        this.initialized = true;
        this.elements.viewer.enableSliderLooping = true;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.onOptionValueChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            this.onOptionValueChanging.bind(this)
          );
        }
        this.elements.viewer
          .querySelectorAll('button[name="previous"], button[name="next"]')
          .forEach((button) => button.addEventListener('click', this.onGalleryButtonClick.bind(this), true));

        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            ?.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        this._initSwipe();

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.onOptionValueChangeUnsubscriber?.();
        this._pendingMediaLoad?.cancel?.();
        this.clearPendingMediaFallback();
      }

      onOptionValueChanging({ data: { event } }) {
        const productInfo = this.closest('product-info');
        if (!productInfo || !productInfo.contains(event.target)) return;

        this.showPendingMediaFallback(this.getActiveMediaItem());
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement?.dataset?.mediaId;
        if (!mediaId || this.isFilteredMediaId(mediaId)) return;

        const activeMedia = this.syncActiveMediaState(mediaId);
        if (!activeMedia) return;
      }

      onGalleryButtonClick(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.navigateGallery(event.currentTarget.name);
      }

      navigateGallery(direction, immediate = false) {
        const visibleMediaItems = this.getVisibleMediaItems();
        if (visibleMediaItems.length < 2) return;

        const activeIndex = Math.max(visibleMediaItems.indexOf(this.getActiveMediaItem()), 0);
        const nextIndex =
          direction === 'next'
            ? (activeIndex + 1) % visibleMediaItems.length
            : (activeIndex - 1 + visibleMediaItems.length) % visibleMediaItems.length;
        const nextMedia = visibleMediaItems[nextIndex];

        if (!nextMedia) return;

        if (immediate) {
          this._activateMedia(nextMedia);
          return;
        }

        this.setActiveMedia(nextMedia.dataset.mediaId, false);
      }

      _initSwipe() {
        const swipeState = {
          startX: 0,
          startY: 0,
          touchId: null,
          pointerId: null,
          horizontal: null,
          maxDx: 0,
          maxDy: 0,
        };

        const resetSwipeState = () => {
          swipeState.startX = 0;
          swipeState.startY = 0;
          swipeState.touchId = null;
          swipeState.pointerId = null;
          swipeState.horizontal = null;
          swipeState.maxDx = 0;
          swipeState.maxDy = 0;
        };

        const getTrackedTouch = (touchList) =>
          Array.from(touchList || []).find((touch) => touch.identifier === swipeState.touchId);

        const beginSwipe = (clientX, clientY, id) => {
          swipeState.startX = clientX;
          swipeState.startY = clientY;
          swipeState.touchId = null;
          swipeState.pointerId = id;
          swipeState.horizontal = null;
          swipeState.maxDx = 0;
          swipeState.maxDy = 0;
        };

        const updateSwipeDirection = (clientX, clientY, event) => {
          if (swipeState.pointerId === null && swipeState.touchId === null) return false;

          const dx = clientX - swipeState.startX;
          const dy = clientY - swipeState.startY;
          swipeState.maxDx = Math.max(swipeState.maxDx, Math.abs(dx));
          swipeState.maxDy = Math.max(swipeState.maxDy, Math.abs(dy));

          if (swipeState.horizontal !== null) {
            if (swipeState.horizontal) event.preventDefault();
            return swipeState.horizontal;
          }

          const absDx = Math.abs(dx);
          const absDy = Math.abs(dy);

          // Only abort to vertical when it is clearly dominant — prevents diagonal
          // gesture starts from misclassifying an intended horizontal swipe.
          if (absDy >= 8 && absDy > absDx * 2.0) {
            swipeState.horizontal = false;
            return false;
          }

          // Commit to horizontal once it is unambiguously leading.
          if (absDx >= 4 && absDx > absDy) {
            swipeState.horizontal = true;
            event.preventDefault();
            return true;
          }

          return false;
        };

        const finishSwipe = (clientX, clientY) => {
          const dx = clientX - swipeState.startX;
          const dy = clientY - swipeState.startY;
          const travel = Math.max(Math.abs(dx), swipeState.maxDx);
          const verticalTravel = Math.max(Math.abs(dy), swipeState.maxDy);
          const shouldNavigate =
            swipeState.horizontal === true && travel > 4 && travel > verticalTravel;
          resetSwipeState();

          if (!shouldNavigate) return;

          this.navigateGallery(dx < 0 ? 'next' : 'previous', true);
        };

       this.elements.viewer.style.touchAction = 'pan-y';
if (this.elements.viewer.slider) this.elements.viewer.slider.style.touchAction = 'pan-y';


        if (window.PointerEvent) {
          this.elements.viewer.addEventListener(
            'pointerdown',
            (event) => {
              if (event.pointerType !== 'touch') return;
              beginSwipe(event.clientX, event.clientY, event.pointerId);
              event.currentTarget.setPointerCapture?.(event.pointerId);
            },
            { passive: true }
          );

          this.elements.viewer.addEventListener(
            'pointermove',
            (event) => {
              if (swipeState.pointerId !== event.pointerId) return;
              updateSwipeDirection(event.clientX, event.clientY, event);
            },
            { passive: false }
          );

          this.elements.viewer.addEventListener(
            'pointerup',
            (event) => {
              if (swipeState.pointerId !== event.pointerId) return;
              finishSwipe(event.clientX, event.clientY);
            },
            { passive: true }
          );

          this.elements.viewer.addEventListener(
            'pointercancel',
            (event) => {
              if (swipeState.pointerId !== event.pointerId) return;
              resetSwipeState();
            },
            { passive: true }
          );

          return;
        }

        this.elements.viewer.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length !== 1) {
              resetSwipeState();
              return;
            }

            const touch = event.touches[0];
            beginSwipe(touch.clientX, touch.clientY, touch.identifier);
            swipeState.touchId = touch.identifier;
          },
          { passive: true }
        );

        this.elements.viewer.addEventListener(
          'touchmove',
          (event) => {
            if (swipeState.touchId === null) return;

            const touch = getTrackedTouch(event.touches);
            if (!touch) return;

            updateSwipeDirection(touch.clientX, touch.clientY, event);
          },
          { passive: false }
        );

        this.elements.viewer.addEventListener(
          'touchend',
          (event) => {
            if (swipeState.touchId === null) return;

            const touch = getTrackedTouch(event.changedTouches) || event.changedTouches[0];
            if (!touch) {
              resetSwipeState();
              return;
            }

            finishSwipe(touch.clientX, touch.clientY);
          },
          { passive: true }
        );

        this.elements.viewer.addEventListener(
          'touchcancel',
          () => {
            resetSwipeState();
          },
          { passive: true }
        );
      }

      setActiveMedia(mediaId) {
        const preferredMediaId = mediaId || this.dataset.selectedMediaId || null;

        // Cancel any pending load only when switching to a different media item.
        // Same-mediaId re-calls (e.g. from syncGalleryCounter polling) skip cancellation
        // to avoid a 1-frame flash from clearing and re-setting the pending fallback CSS.
        if (this._pendingMediaLoad?.mediaId !== preferredMediaId) {
          this._pendingMediaLoad?.cancel();
        }

        const activeItemsBeforeFilter = this.getMediaItems().filter((mediaItem) =>
          mediaItem.classList.contains('is-active')
        );
        const stableActiveMedia =
          activeItemsBeforeFilter.find((mediaItem) => mediaItem.dataset.mediaId !== preferredMediaId) ||
          activeItemsBeforeFilter[0] ||
          null;

        this.applyVariantMediaFilter(preferredMediaId, stableActiveMedia?.dataset.mediaId);

        const visibleMediaItems = this.getVisibleMediaItems();
        if (!visibleMediaItems.length) return;

        const activeMedia =
          visibleMediaItems.find((mediaItem) => mediaItem.dataset.mediaId === preferredMediaId) ||
          visibleMediaItems.find((mediaItem) => mediaItem.classList.contains('is-active')) ||
          visibleMediaItems[0];

        const img = activeMedia.querySelector('.product__media img');
        const hasStableActiveMedia = stableActiveMedia && stableActiveMedia !== activeMedia;
        const alreadyActive = activeMedia.classList.contains('is-active') && !hasStableActiveMedia;

        if (img && !img.complete) {
          if (alreadyActive) {
            // DOM-swap path: Dawn's viewTransition inserts the new gallery into the DOM
            // (triggering connectedCallback) before hiding the old gallery. The old gallery
            // is still live — read its active image's currentSrc and pin it as a background
            // so the user sees the previous image instead of a blank while the new image loads.
            //
            // Same-mediaId re-entry (e.g. syncGalleryCounter polling): pending state is already
            // set for this mediaId — skip re-setup to avoid clearing the fallback CSS and just
            // update the counter/buttons by falling through to _activateMedia.
            if (this._pendingMediaLoad?.mediaId === activeMedia.dataset.mediaId) {
              this._activateMedia(activeMedia);
              return;
            }

            let cleanedUp = false;
            const mediaEl = activeMedia.querySelector('.product__media');
            const viewerEl = this.elements.viewer;
            const mediaWrapper = this.closest('.product__media-wrapper');
            const oldGallery =
              (this.nextElementSibling?.matches?.('media-gallery') && this.nextElementSibling) ||
              Array.from(this.parentElement?.children || []).find(
                (node) => node !== this && node.matches?.('media-gallery') && node.style.display === 'none'
              ) ||
              null;
            const oldWrapper = oldGallery?.closest('.product__media-wrapper');
            const oldActiveItem = oldGallery?.querySelector('[data-media-id].is-active');
            const oldMediaEl = oldGallery?.querySelector('[data-media-id].is-active .product__media');
            const activeMediaContainer = activeMedia.querySelector('.product-media-container');
            const oldMediaContainer = oldGallery?.querySelector('[data-media-id].is-active .product-media-container');
            const oldImg = oldGallery?.querySelector('[data-media-id].is-active .product__media img');
            const oldSrc = oldImg?.currentSrc || oldImg?.src || null;
            const oldHeight = Math.round(oldMediaEl?.getBoundingClientRect?.().height || 0);
            const oldItemHeight = Math.round(oldActiveItem?.getBoundingClientRect?.().height || 0);
            const oldWrapperHeight = Math.round(oldWrapper?.getBoundingClientRect?.().height || 0);
            const oldAspectRatio =
              oldMediaContainer?.style?.getPropertyValue('--aspect-ratio') ||
              oldMediaContainer?.style?.getPropertyValue('--preview-ratio') ||
              oldMediaContainer?.style?.getPropertyValue('--ratio') ||
              null;
            const spinner = activeMedia.querySelector('.loading__spinner');

            // Reuse any pre-emptive pending fallback (set by onOptionValueChanging before the
            // fetch) rather than clearing and re-setting it, which would cause a 1-frame flash.
            if (!this._pendingMediaFallbackCleanup) {
              this.showPendingMediaFallback(null, oldSrc);
            }
            if (mediaEl && oldSrc) {
              mediaEl.style.cssText = `background-image:url("${oldSrc}");background-size:contain;background-repeat:no-repeat;background-position:center${oldHeight ? `;min-height:${oldHeight}px` : ''}`;
            }
            if (activeMediaContainer && oldAspectRatio) {
              activeMediaContainer.style.setProperty('--aspect-ratio', oldAspectRatio.trim());
            }
            if (oldItemHeight) {
              activeMedia.style.minHeight = `${oldItemHeight}px`;
              viewerEl.style.minHeight = `${oldItemHeight}px`;
            }
            if (oldWrapperHeight && mediaWrapper) mediaWrapper.style.minHeight = `${oldWrapperHeight}px`;
            spinner?.classList.remove('hidden');

            const cleanup = () => {
              if (cleanedUp) return;
              cleanedUp = true;
              this._pendingMediaLoad = null;
              this.clearPendingMediaFallback();
              if (mediaEl) mediaEl.style.cssText = '';
              if (activeMediaContainer) activeMediaContainer.style.removeProperty('--aspect-ratio');
              activeMedia.style.minHeight = '';
              viewerEl.style.minHeight = '';
              if (mediaWrapper) mediaWrapper.style.minHeight = '';
              spinner?.classList.add('hidden');
              this.clearTransitionState();
            };

            const timeout = setTimeout(cleanup, 3000);
            img.addEventListener('load', cleanup, { once: true });
            img.addEventListener('error', cleanup, { once: true });

            this._pendingMediaLoad = {
              mediaId: activeMedia.dataset.mediaId,
              cancel: () => {
                clearTimeout(timeout);
                cleanup();
                img.removeEventListener('load', cleanup);
                img.removeEventListener('error', cleanup);
              },
            };
            // Fall through to _activateMedia so counter/buttons update immediately.
          } else {
            // Gallery-switch path: activate the requested slide immediately so gallery
            // navigation always advances, but pin the previously visible image behind it
            // until the new slide's image has loaded. This avoids blank frames without
            // making next/previous navigation wait on a preload timeout.
            let cancelled = false;
            let cleanedUp = false;
            const originalLoading = img.getAttribute('loading');
            const mediaEl = activeMedia.querySelector('.product__media');
            const viewerEl = this.elements.viewer;
            const mediaWrapper = this.closest('.product__media-wrapper');
            const activeMediaContainer = activeMedia.querySelector('.product-media-container');
            const stableMediaEl = stableActiveMedia?.querySelector('.product__media');
            const stableWrapper = stableActiveMedia?.closest('.product__media-wrapper');
            const stableMediaContainer = stableActiveMedia?.querySelector('.product-media-container');
            const oldSrc =
              stableActiveMedia?.querySelector('.product__media img')?.currentSrc ||
              stableActiveMedia?.querySelector('.product__media img')?.src ||
              null;
            const oldHeight = Math.round(stableMediaEl?.getBoundingClientRect?.().height || 0);
            const oldItemHeight = Math.round(stableActiveMedia?.getBoundingClientRect?.().height || 0);
            const oldWrapperHeight = Math.round(stableWrapper?.getBoundingClientRect?.().height || 0);
            const oldAspectRatio =
              stableMediaContainer?.style?.getPropertyValue('--aspect-ratio') ||
              stableMediaContainer?.style?.getPropertyValue('--preview-ratio') ||
              stableMediaContainer?.style?.getPropertyValue('--ratio') ||
              null;
            const spinner = activeMedia.querySelector('.loading__spinner');

            this.showPendingMediaFallback(stableActiveMedia);
            if (mediaEl && oldSrc) {
              mediaEl.style.cssText = `background-image:url("${oldSrc}");background-size:contain;background-repeat:no-repeat;background-position:center${oldHeight ? `;min-height:${oldHeight}px` : ''}`;
            }
            if (activeMediaContainer && oldAspectRatio) {
              activeMediaContainer.style.setProperty('--aspect-ratio', oldAspectRatio.trim());
            }
            if (oldItemHeight) {
              activeMedia.style.minHeight = `${oldItemHeight}px`;
              viewerEl.style.minHeight = `${oldItemHeight}px`;
            }
            if (oldWrapperHeight && mediaWrapper) mediaWrapper.style.minHeight = `${oldWrapperHeight}px`;
            spinner?.classList.remove('hidden');
            img.setAttribute('loading', 'eager');
            this._activateMedia(activeMedia);

            const cleanup = () => {
              if (cleanedUp) return;
              cleanedUp = true;
              this._pendingMediaLoad = null;
              this.clearPendingMediaFallback();
              if (mediaEl) mediaEl.style.cssText = '';
              if (activeMediaContainer) activeMediaContainer.style.removeProperty('--aspect-ratio');
              activeMedia.style.minHeight = '';
              viewerEl.style.minHeight = '';
              if (mediaWrapper) mediaWrapper.style.minHeight = '';
              spinner?.classList.add('hidden');
              if (originalLoading === null) {
                img.removeAttribute('loading');
              } else {
                img.setAttribute('loading', originalLoading);
              }
              this.clearTransitionState();
            };

            const onSettled = () => {
              if (cancelled) {
                cleanup();
                return;
              }
              cleanup();
            };

            img.addEventListener('load', onSettled, { once: true });
            img.addEventListener('error', onSettled, { once: true });
            img.decode?.().then(onSettled).catch(() => {});
            const timeout = setTimeout(onSettled, 3000);

            this._pendingMediaLoad = {
              mediaId: activeMedia.dataset.mediaId,
              cancel: () => {
                cancelled = true;
                clearTimeout(timeout);
                cleanup();
                img.removeEventListener('load', onSettled);
                img.removeEventListener('error', onSettled);
              },
            };
            return;
          }
        }

        this._activateMedia(activeMedia);
      }

      _activateMedia(activeMedia) {
        if (activeMedia?.dataset?.mediaId) {
          this.dataset.selectedMediaId = activeMedia.dataset.mediaId;
        }

        this.applyVariantMediaFilter(activeMedia.dataset.mediaId);
        activeMedia = this.syncActiveMediaState(activeMedia.dataset.mediaId);
        if (!activeMedia) return;

        this.preventStickyHeader();
        window.setTimeout(() => {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'instant' });

          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });

        this.playActiveMedia(activeMedia);
        const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, this.getVisibleMediaItems().indexOf(activeMedia) + 1);
      }

      applyVariantMediaFilter(preserveMediaId = null, transitionMediaId = null) {
        const mediaItems = this.getMediaItems();
        const matchingMediaItems = this.getMatchingMediaItems(mediaItems);
        const visibleMediaIds = new Set(matchingMediaItems.map((mediaItem) => mediaItem.dataset.mediaId));
        const resolvedMediaId = preserveMediaId || this.dataset.selectedMediaId || null;

        // Variant featured media always takes precedence over the alt-text filter
        if (resolvedMediaId) visibleMediaIds.add(resolvedMediaId);

        // Keep the displayed item in the visible set during the transition
        // so it is never set to hidden=true while it still holds is-active.
        // Newly fetched variant media can arrive with is-active from the section
        // HTML, so the caller passes the prior displayed media explicitly.
        if (transitionMediaId) visibleMediaIds.add(transitionMediaId);

        if (!visibleMediaIds.size) {
          if (resolvedMediaId) visibleMediaIds.add(resolvedMediaId);
          mediaItems
            .filter((mediaItem) => mediaItem.classList.contains('is-active'))
            .forEach((mediaItem) => visibleMediaIds.add(mediaItem.dataset.mediaId));
        }

        if (!visibleMediaIds.size) {
          mediaItems.forEach((mediaItem) => visibleMediaIds.add(mediaItem.dataset.mediaId));
        }

        mediaItems.forEach((mediaItem) => {
          const isFiltered = !visibleMediaIds.has(mediaItem.dataset.mediaId);
          mediaItem.hidden = isFiltered;
          mediaItem.classList.toggle('is-filtered', isFiltered);
        });

        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((thumbnail) => {
          const isFiltered = !visibleMediaIds.has(thumbnail.dataset.target);
          thumbnail.hidden = isFiltered;
          thumbnail.classList.toggle('is-filtered', isFiltered);
        });

        this.elements.viewer.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
      }

      getMatchingMediaItems(mediaItems) {
        const selectedTerms = this.getSelectedMediaTerms();
        if (!Object.keys(selectedTerms).length) return mediaItems;

        const hasSpecificTerms = selectedTerms.color || selectedTerms.hardware;
        const componentMatches = hasSpecificTerms
          ? mediaItems.filter((mediaItem) => {
              const mediaColor = this.normalizeText(mediaItem.dataset.mediaColor);
              const mediaHardware = this.normalizeText(mediaItem.dataset.mediaHardware);

              const colorMatch = !selectedTerms.color || mediaColor === selectedTerms.color;
              const hardwareMatch = !selectedTerms.hardware || mediaHardware === selectedTerms.hardware;

              // If it matches both selected terms exactly, it's a winner
              if (colorMatch && hardwareMatch) return true;

              // Fallback: if media only has one term but it matches one of our specific selections
              // (e.g. generic product with only Color option)
              if (!mediaHardware && colorMatch && !selectedTerms.hardware) return true;

              return false;
            })
          : [];

        const fullTitleMatches = selectedTerms.fullTitle
          ? mediaItems.filter((mediaItem) => this.normalizeText(mediaItem.dataset.mediaAlt) === selectedTerms.fullTitle)
          : [];

        if (componentMatches.length && fullTitleMatches.length) {
          const exactComponentMatches = componentMatches.filter((mediaItem) =>
            fullTitleMatches.includes(mediaItem)
          );
          if (exactComponentMatches.length) return exactComponentMatches;
        }

        if (componentMatches.length) return componentMatches;
        return fullTitleMatches.length ? fullTitleMatches : mediaItems;
      }

      getSelectedMediaTerms() {
        const productInfo = this.closest('product-info');
        const fallbackColor = this.normalizeText(this.dataset.initialColor);
        const fallbackHardware = this.normalizeText(this.dataset.initialHardware);
        const fallbackFullTitle = this.normalizeText(this.dataset.initialFullTitle);
        const resolvedColor = this.normalizeText(this.dataset.resolvedMediaColor);
        const resolvedHardware = this.normalizeText(this.dataset.resolvedMediaHardware);
        const resolvedFullTitleFromDataset = this.normalizeText(this.dataset.resolvedMediaTitle);

        // During a media transition the freshly rendered gallery HTML is the
        // authoritative source of filter state. Prefer the server-resolved
        // dataset values over the transient picker DOM so a mid-flight DOM
        // re-read cannot produce a stale or mismatched filter.
        const isInTransition =
          this.hasAttribute('data-media-transition') ||
          !!productInfo?.hasAttribute('data-media-transition');

        const getVal = (handles) => {
          const group = handles
            .map((h) => productInfo?.querySelector(`.product-form__input[data-option-handle="${h}"]`))
            .find(Boolean);
          if (!group) return null;
          const val = group.querySelector('input:checked')?.value ||
                      group.querySelector('select')?.value ||
                      group.querySelector('[data-selected-value]')?.textContent;
          if (val == null || String(val).trim() === '') return null;
          return this.normalizeText(val);
        };

        let finalColor, finalHardware, resolvedFullTitle;

        if (isInTransition && (resolvedColor || resolvedHardware || resolvedFullTitleFromDataset)) {
          finalColor = resolvedColor || fallbackColor;
          finalHardware = resolvedHardware || fallbackHardware;
          resolvedFullTitle = resolvedFullTitleFromDataset || fallbackFullTitle;
        } else {
          const color = getVal(this.colorOptionHandles);
          const hardware = getVal(this.hardwareColorOptionHandles);
          const fullTitle = this.getSelectedVariantTitle();
          finalColor = color === null ? resolvedColor || fallbackColor : color;
          finalHardware = hardware === null ? resolvedHardware || fallbackHardware : hardware;
          resolvedFullTitle = fullTitle || resolvedFullTitleFromDataset || fallbackFullTitle;
        }

        const terms = {};
        if (finalColor && !this.genericMediaTerms.includes(finalColor)) terms.color = finalColor;
        if (finalHardware && !this.genericMediaTerms.includes(finalHardware)) terms.hardware = finalHardware;
        if (resolvedFullTitle) terms.fullTitle = resolvedFullTitle;

        return terms;
      }

      getSelectedVariantTitle() {
        const selectedVariant = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        if (!selectedVariant) return '';

        try {
          const parsedVariant = JSON.parse(selectedVariant);
          const variantTitle = this.normalizeText(parsedVariant?.public_title || parsedVariant?.title);
          return variantTitle && variantTitle !== 'default title' ? variantTitle : '';
        } catch (error) {
          console.error('Unable to parse selected variant JSON', error);
          return '';
        }
      }

      getMediaItems() {
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-list > [data-media-id]'));
      }

      getVisibleMediaItems() {
        return this.getMediaItems().filter((mediaItem) => !mediaItem.hidden && !mediaItem.classList.contains('is-filtered'));
      }

      getActiveMediaItem() {
        return this.elements.viewer.querySelector('.product__media-list > [data-media-id].is-active');
      }

      clearTransitionState() {
        if (this._pendingMediaLoad || this._pendingMediaFallbackCleanup) return;
        if (!this.hasAttribute('data-media-transition') && !this.closest('product-info')?.hasAttribute('data-media-transition'))
          return;
        window.cancelAnimationFrame(this._transitionStateFrame);
        this._transitionStateFrame = window.requestAnimationFrame(() => {
          this._transitionStateFrame = null;
          if (this._pendingMediaLoad || this._pendingMediaFallbackCleanup) return;
          this.removeAttribute('data-media-transition');
          this.closest('product-info')?.removeAttribute('data-media-transition');
        });
      }

      syncActiveMediaState(preferredMediaId = null) {
        const visibleMediaItems = this.getVisibleMediaItems();
        if (!visibleMediaItems.length) return null;

        const activeMedia =
          visibleMediaItems.find((mediaItem) => mediaItem.dataset.mediaId === preferredMediaId) ||
          visibleMediaItems.find((mediaItem) => mediaItem.classList.contains('is-active')) ||
          visibleMediaItems[0];

        // Add is-active to the new item before removing it elsewhere so the gallery
        // never enters a blank "no active slide" state mid-transition.
        activeMedia.classList.add('is-active');
        this.dataset.selectedMediaId = activeMedia.dataset.mediaId;
        this.getMediaItems().forEach((mediaItem) => {
          if (mediaItem !== activeMedia) mediaItem.classList.remove('is-active');
        });

        this.updateGalleryCounter(activeMedia);
        this.updateGalleryButtons(activeMedia);

        const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.clearTransitionState();
        return activeMedia;
      }

      isFilteredMediaId(mediaId) {
        const mediaItem = this.elements.viewer.querySelector(`.product__media-list > [data-media-id="${mediaId}"]`);
        return !mediaItem || mediaItem.hidden || mediaItem.classList.contains('is-filtered');
      }

      normalizeText(value) {
        return String(value || '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, ' ')
          .trim()
          .replace(/\s+/g, ' ');
      }

      updateGalleryCounter(activeMedia) {
        const visibleMediaItems = this.getVisibleMediaItems();
        const current = this.elements.viewer.querySelector('.slider-counter--current');
        const total = this.elements.viewer.querySelector('.slider-counter--total');
        if (!current || !total || !activeMedia) return;

        const currentValue = String(visibleMediaItems.indexOf(activeMedia) + 1);
        const totalValue = String(visibleMediaItems.length);
        if (current.textContent !== currentValue) current.textContent = currentValue;
        if (total.textContent !== totalValue) total.textContent = totalValue;
      }

      updateGalleryButtons(activeMedia) {
        const previousButton = this.elements.viewer.querySelector('button[name="previous"]');
        const nextButton = this.elements.viewer.querySelector('button[name="next"]');

        previousButton?.removeAttribute('disabled');
        nextButton?.removeAttribute('disabled');
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;

        const announceImage = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        if (image.complete) {
          announceImage();
        } else {
          image.addEventListener('load', announceImage, { once: true });
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      showPendingMediaFallback(stableActiveMedia, fallbackSrc = null) {
        if (!this.elements.viewer) return () => {};

        const img = stableActiveMedia?.querySelector('.product__media img');
        const src = fallbackSrc || img?.currentSrc || img?.src;
        const mediaWrapper = this.closest('.product__media-wrapper');
        const thumbnails = this.elements.thumbnails;
        const thumbnailList = thumbnails?.querySelector('.thumbnail-list');
        const rawGalleryHeight = Math.round(this.getBoundingClientRect?.().height || 0);
        const stableGalleryHeight = rawGalleryHeight > 50
          ? rawGalleryHeight
          : (parseInt(this.dataset.prevGalleryHeight || '0') || 0);
        const stableItemHeight = Math.round(stableActiveMedia?.getBoundingClientRect?.().height || 0);
        const stableWrapperHeight = Math.round(mediaWrapper?.getBoundingClientRect?.().height || 0);
        const stableThumbnailsHeight = Math.round(thumbnails?.getBoundingClientRect?.().height || 0);
        const stableThumbnailListHeight = Math.round(thumbnailList?.getBoundingClientRect?.().height || 0);
        const stableViewerRect = this.elements.viewer.getBoundingClientRect?.();
        const stableThumbnailsRect = thumbnails?.getBoundingClientRect?.();
        const thumbnailsGap =
          stableViewerRect && stableThumbnailsRect
            ? Math.max(0, Math.round(stableThumbnailsRect.top - stableViewerRect.bottom))
            : 0;
        const galleryMinHeightValue = stableGalleryHeight ? `${stableGalleryHeight}px` : '';
        const viewerMinHeightValue = stableItemHeight ? `${stableItemHeight}px` : '';
        const wrapperMinHeightValue = stableWrapperHeight ? `${stableWrapperHeight}px` : viewerMinHeightValue;
        const thumbnailsMinHeightValue = stableThumbnailsHeight ? `${stableThumbnailsHeight}px` : '';
        const thumbnailListMinHeightValue = stableThumbnailListHeight ? `${stableThumbnailListHeight}px` : '';
        const thumbnailsMarginTopValue = thumbnailsGap ? `${thumbnailsGap}px` : '';

        this.clearPendingMediaFallback();
        if (src) this.elements.viewer.style.setProperty('--variant-media-pending-image', `url("${src}")`);
        if (galleryMinHeightValue) {
          this.style.minHeight = galleryMinHeightValue;
        }
        if (viewerMinHeightValue) {
          this.elements.viewer.style.minHeight = viewerMinHeightValue;
        }
        if (mediaWrapper && wrapperMinHeightValue) {
          mediaWrapper.style.minHeight = wrapperMinHeightValue;
        }
        if (thumbnails && thumbnailsMinHeightValue) {
          thumbnails.style.minHeight = thumbnailsMinHeightValue;
          if (thumbnailsMarginTopValue) {
            thumbnails.style.marginTop = thumbnailsMarginTopValue;
          }
        }
        if (thumbnailList && thumbnailListMinHeightValue) {
          thumbnailList.style.minHeight = thumbnailListMinHeightValue;
        }
        this.classList.add('media-gallery--variant-pending');

        const timeout = setTimeout(() => this.clearPendingMediaFallback(), 3000);
        this._pendingMediaFallbackCleanup = () => {
          clearTimeout(timeout);
          this.classList.remove('media-gallery--variant-pending');
          this.elements.viewer.style.removeProperty('--variant-media-pending-image');
          if (galleryMinHeightValue && this.style.minHeight === galleryMinHeightValue) {
            this.style.minHeight = '';
          }
          if (viewerMinHeightValue && this.elements.viewer.style.minHeight === viewerMinHeightValue) {
            this.elements.viewer.style.minHeight = '';
          }
          if (mediaWrapper && wrapperMinHeightValue && mediaWrapper.style.minHeight === wrapperMinHeightValue) {
            mediaWrapper.style.minHeight = '';
          }
          if (thumbnails && thumbnailsMinHeightValue && thumbnails.style.minHeight === thumbnailsMinHeightValue) {
            thumbnails.style.minHeight = '';
          }
          if (thumbnails && thumbnailsMarginTopValue && thumbnails.style.marginTop === thumbnailsMarginTopValue) {
            thumbnails.style.marginTop = '';
          }
          if (
            thumbnailList &&
            thumbnailListMinHeightValue &&
            thumbnailList.style.minHeight === thumbnailListMinHeightValue
          ) {
            thumbnailList.style.minHeight = '';
          }
        };
        return this._pendingMediaFallbackCleanup;
      }

      clearPendingMediaFallback() {
        this._pendingMediaFallbackCleanup?.();
        this._pendingMediaFallbackCleanup = null;
      }

      clearPendingMediaFallbackWhenVisible(activeMedia, startedAt = performance.now(), visibleSince = null) {
        if (!this._pendingMediaFallbackCleanup) return;

        const now = performance.now();
        const styles = window.getComputedStyle(activeMedia);
        const galleryStyles = window.getComputedStyle(this);
        const rect = activeMedia.getBoundingClientRect();
        const isVisible =
          galleryStyles.display !== 'none' &&
          galleryStyles.visibility !== 'hidden' &&
          styles.display !== 'none' &&
          styles.visibility !== 'hidden' &&
          rect.width > 1 &&
          rect.height > 1;

        const nextVisibleSince = isVisible ? visibleSince || now : null;

        if ((nextVisibleSince && now - nextVisibleSince > 700) || now - startedAt > 3000) {
          this.clearPendingMediaFallback();
          return;
        }

        window.requestAnimationFrame(() =>
          this.clearPendingMediaFallbackWhenVisible(activeMedia, startedAt, nextVisibleSince)
        );
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
